import { useEffect } from 'react';

import { PunchOverlay } from './components/PunchOverlay';
import { StatusBar } from './components/StatusBar';
import { UpdateOverlay } from './components/UpdateOverlay';
import { PairingScreen } from './device/PairingScreen';
import { useDeviceToken } from './device/useDeviceToken';
import { useCamera } from './photo/useCamera';
import { PinPad } from './pinpad/PinPad';
import { useClockSkew } from './skew/useClockSkew';
import { kioskEngine } from './sync/syncEngine';
import { useKioskState } from './sync/useKioskState';

/**
 * The live clock screen — only mounted once the device is paired. With
 * photo-on-punch enabled the camera feed fills the screen as the background and
 * the keypad floats over it.
 */
function ClockScreen(): JSX.Element {
  const state = useKioskState();
  const skew = useClockSkew();
  // Warm front-facing stream only when the tenant has photo-on-punch enabled.
  const camera = useCamera(state.photoEnabled);

  useEffect(() => {
    kioskEngine.start();
    return () => {
      kioskEngine.stop();
    };
  }, []);

  const handlePin = (pin: string): void => {
    // Pass the capturer ONLY when enabled; the engine grabs one frame at submit
    // and NEVER lets capture block or delay the punch.
    void kioskEngine.submitPin(pin, state.photoEnabled ? camera.capture : undefined);
  };

  return (
    <div className={state.photoEnabled ? 'kiosk kiosk--camera' : 'kiosk'}>
      {state.photoEnabled && (
        <>
          <video
            ref={camera.videoRef}
            className="kiosk__camera"
            autoPlay
            muted
            playsInline
            aria-hidden="true"
          />
          <div className="kiosk__scrim" aria-hidden="true" />
        </>
      )}

      <header className="kiosk__header">
        <div className="kiosk__dept">{state.departmentName ?? 'WorkLogix Clock'}</div>
        <StatusBar
          online={state.online}
          syncing={state.syncing}
          serverReachable={state.serverReachable}
          queuedCount={state.queuedCount}
          skewWarn={skew.warn}
          skewMs={skew.skewMs}
        />
      </header>

      <main className="kiosk__body">
        <PinPad onSubmit={handlePin} />
      </main>

      <div className="kiosk__toasts">
        {state.echo?.pending === true && (
          <div className="pill" role="status" aria-live="polite">
            {state.echo.message}
          </div>
        )}
        {state.photoWarning !== null && (
          <div className="pill pill--warn" role="alert" aria-live="assertive">
            {state.photoWarning}
          </div>
        )}
      </div>

      <PunchOverlay
        echo={state.echo}
        onDismiss={() => {
          kioskEngine.clearEcho();
        }}
      />
      <UpdateOverlay busy={state.echo !== null} />
    </div>
  );
}

/**
 * Root of the kiosk PWA. Gates on the paired device token: no token => pairing
 * screen; paired => the live clock. There is deliberately NO MSAL AuthProvider
 * and NO Entra sign-in — this is a device+PIN app.
 */
export function KioskApp(): JSX.Element {
  const deviceToken = useDeviceToken();
  return deviceToken === null ? <PairingScreen /> : <ClockScreen />;
}
